import { apiClient, unwrap } from './client';
import type { ApiResponse, PagedResult } from '@/types/api';
import type {
  AssignEnterpriseRequest,
  CreateUserRequest,
  ResetPasswordRequest,
  RoleDto,
  UpdateUserRequest,
  UserDto,
  UserListRequest,
} from '@/types/user';

export const usersApi = {
  list(request: UserListRequest = {}): Promise<PagedResult<UserDto>> {
    return unwrap(apiClient.get<ApiResponse<PagedResult<UserDto>>>('/users', { params: request }));
  },
  getById(id: number): Promise<UserDto> {
    return unwrap(apiClient.get<ApiResponse<UserDto>>(`/users/${id}`));
  },
  create(request: CreateUserRequest): Promise<UserDto> {
    return unwrap(apiClient.post<ApiResponse<UserDto>>('/users', request));
  },
  update(id: number, request: UpdateUserRequest): Promise<UserDto> {
    return unwrap(apiClient.put<ApiResponse<UserDto>>(`/users/${id}`, request));
  },
  deactivate(id: number): Promise<void> {
    return apiClient.delete(`/users/${id}`).then(() => undefined);
  },
  reactivate(id: number): Promise<void> {
    return apiClient.post(`/users/${id}/reactivate`).then(() => undefined);
  },
  unlock(id: number): Promise<void> {
    return apiClient.post(`/users/${id}/unlock`).then(() => undefined);
  },
  resetPassword(id: number, request: ResetPasswordRequest): Promise<void> {
    return apiClient.post(`/users/${id}/reset-password`, request).then(() => undefined);
  },
  assignEnterprise(id: number, request: AssignEnterpriseRequest): Promise<UserDto> {
    return unwrap(apiClient.post<ApiResponse<UserDto>>(`/users/${id}/enterprises`, request));
  },
  removeEnterprise(id: number, enterpriseId: number): Promise<UserDto> {
    return unwrap(apiClient.delete<ApiResponse<UserDto>>(`/users/${id}/enterprises/${enterpriseId}`));
  },
  assignMdm(id: number, mdmId: number): Promise<UserDto> {
    return unwrap(apiClient.post<ApiResponse<UserDto>>(`/users/${id}/mdms`, { mdmId }));
  },
  removeMdm(id: number, mdmId: number): Promise<UserDto> {
    return unwrap(apiClient.delete<ApiResponse<UserDto>>(`/users/${id}/mdms/${mdmId}`));
  },
};

export const rolesApi = {
  list(): Promise<RoleDto[]> {
    return unwrap(apiClient.get<ApiResponse<RoleDto[]>>('/roles'));
  },
};
